import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import type {
  DeveloperMetricsRun,
  UnmatchedDeveloper,
} from "views/reports/DeveloperMetrics/DeveloperMetricsStore";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * Administration → Git Identities: which git email is whose.
 *
 * The developer-metrics intake maps a run's commit emails to people through
 * this table (API `developer-git-identities`, admin-only; plan
 * 2026-10-20_FROM-REPORT-METRICS_developer-metrics-service.md §2.5). The
 * latest run's `unmatched` list — developers whose emails mapped to nobody —
 * is shown here as the worklist, each email one click from being linked.
 *
 * One email belongs to one person: the API refuses a second claim with a 409
 * naming who holds it, and this screen shows that message as it is. Changes
 * are not retroactive — the next run resolves against the table as it stands.
 */

/** One identity as the API returns it. */
export interface GitIdentity {
  id: number;
  userId: number;
  email: string;
  displayName: string | null;
  createdAt: string | null;
  createdBy: number | null;
  updatedAt: string | null;
  updatedBy: number | null;
  userEmail: string | null;
  firstName: string | null;
  lastName: string | null;
  userActive: boolean;
}

export interface UserSummary {
  id: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  isValidated?: boolean;
}

/** One unlinked email from the latest run, with the developer the collector called it. */
export interface UnlinkedEmail {
  email: string;
  developerKey: string;
}

/** The API's cap on one find (`$limit` maximum). */
export const IDENTITIES_SERVER_LIMIT = 1000;

/** Trimmed and lower-cased — the spelling the API stores. */
export const normaliseGitEmail = (raw: string): string =>
  raw.trim().toLowerCase();

export const personLabel = (
  p: Pick<UserSummary, "email" | "firstName" | "lastName">,
): string => {
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  return name && p.email ? `${name} — ${p.email}` : name || p.email || "";
};

/**
 * The worklist: every email of every unmatched developer in the latest run
 * that is still not linked to anyone.
 */
export function unlinkedEmails(
  unmatched: UnmatchedDeveloper[],
  linked: ReadonlySet<string>,
): UnlinkedEmail[] {
  const out: UnlinkedEmail[] = [];
  const seen = new Set<string>();
  for (const dev of unmatched) {
    for (const raw of dev.emails) {
      const email = normaliseGitEmail(raw);
      if (!email || linked.has(email) || seen.has(email)) continue;
      seen.add(email);
      out.push({ email, developerKey: dev.key });
    }
  }
  return out;
}

interface IdentityForm {
  /** The identity being edited; null when adding. */
  id: number | null;
  userId: number | null;
  email: string;
  displayName: string;
}

const EMPTY_FORM: IdentityForm = {
  id: null,
  userId: null,
  email: "",
  displayName: "",
};

class GitIdentitiesStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  searchQuery = "";

  form: IdentityForm = { ...EMPTY_FORM };
  formError: string | null = null;
  saving = false;

  removeTarget: GitIdentity | null = null;
  removing = false;
  /** Why the last Remove was refused — the remove dialog's route throws it, so
   *  it shows in that dialog's footer while the dialog stays open. */
  removeError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private storeFor(path: string, key: string): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore(path) ??
        (this.rootStore as unknown as Record<string, unknown>)[
          key
        ]) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get service(): ServiceStoreType | null {
    return this.storeFor("developer-git-identities", "developerGitIdentitiesStore");
  }

  private get runsService(): ServiceStoreType | null {
    return this.storeFor("developer-metrics/runs", "developerMetricsRunsStore");
  }

  private get usersService(): ServiceStoreType | null {
    return (this.rootStore.userStore as unknown as ServiceStoreType | null) ?? null;
  }

  // ---- Derived data (entities live in the ServiceStore caches) -------------

  get rows(): GitIdentity[] {
    return (this.service?.entities ?? []) as unknown as GitIdentity[];
  }

  get visibleRows(): GitIdentity[] {
    const q = this.searchQuery.trim().toLowerCase();
    const rows = !q
      ? this.rows
      : this.rows.filter((r) =>
          [r.email, r.displayName, r.firstName, r.lastName, r.userEmail]
            .join(" ")
            .toLowerCase()
            .includes(q),
        );
    return [...rows].sort(
      (a, b) =>
        personLabel(a).localeCompare(personLabel(b)) ||
        a.email.localeCompare(b.email),
    );
  }

  get truncated(): boolean {
    return this.rows.length >= IDENTITIES_SERVER_LIMIT;
  }

  get latestRun(): DeveloperMetricsRun | null {
    const runs = (this.runsService?.entities ??
      []) as unknown as DeveloperMetricsRun[];
    return runs.find((r) => r.latest) ?? null;
  }

  get worklist(): UnlinkedEmail[] {
    const linked = new Set(this.rows.map((r) => normaliseGitEmail(r.email)));
    return unlinkedEmails(this.latestRun?.unmatched ?? [], linked);
  }

  /** People for the picker, by name. Deactivated people stay pickable: their past commits are still theirs. */
  get users(): UserSummary[] {
    return (
      (this.usersService?.entities ?? []) as unknown as Array<
        UserSummary & { id: number | string }
      >
    )
      .filter((u) => !!u.email && Number.isInteger(Number(u.id)))
      .map((u) => ({ ...u, id: Number(u.id) }))
      .sort((a, b) => personLabel(a).localeCompare(personLabel(b)));
  }

  /** Someone else already holds the email in the form. */
  get formDuplicate(): GitIdentity | null {
    const email = normaliseGitEmail(this.form.email);
    if (!email) return null;
    return (
      this.rows.find(
        (r) => normaliseGitEmail(r.email) === email && r.id !== this.form.id,
      ) ?? null
    );
  }

  get canSubmitForm(): boolean {
    const email = normaliseGitEmail(this.form.email);
    return (
      this.form.userId != null &&
      email.length >= 3 &&
      !/[\s<>]/.test(email) &&
      !this.formDuplicate &&
      !this.saving
    );
  }

  setSearchQuery(value: string) {
    this.searchQuery = value;
  }

  // ---- Loading --------------------------------------------------------------

  async load() {
    await Promise.all([this.loadIdentities(), this.loadContext()]);
  }

  async loadIdentities() {
    this.loading = true;
    this.error = null;
    try {
      await findOrThrow(this.service, {
        query: { $limit: IDENTITIES_SERVER_LIMIT },
      });
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load git identities");
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Best-effort: the picker's people and the latest run's worklist. */
  async loadContext() {
    try {
      await Promise.all([
        this.usersService?.find({ query: { $limit: 500 } }),
        this.runsService?.find({ query: { $limit: 1 } }),
      ]);
    } catch {
      // The table still reads without them.
    }
  }

  // ---- Add / edit dialog (route-registered; see gitIdentities.Route.ts) ------

  openAdd(prefill: Partial<Pick<IdentityForm, "email" | "displayName">> = {}) {
    this.form = { ...EMPTY_FORM, ...prefill };
    this.formError = null;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "GitIdentityDialog",
    });
  }

  openEdit(row: GitIdentity) {
    this.form = {
      id: row.id,
      userId: row.userId,
      email: row.email,
      displayName: row.displayName ?? "",
    };
    this.formError = null;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "GitIdentityDialog",
    });
  }

  setFormUser(userId: number | null) {
    this.form = { ...this.form, userId };
  }

  setFormEmail(email: string) {
    this.form = { ...this.form, email };
  }

  setFormDisplayName(displayName: string) {
    this.form = { ...this.form, displayName };
  }

  /** @returns true when the write landed (the route throws formError otherwise, which keeps the dialog open). */
  async submitForm(): Promise<boolean> {
    if (!this.canSubmitForm) return false;
    this.saving = true;
    this.formError = null;
    const body = {
      userId: this.form.userId,
      email: normaliseGitEmail(this.form.email),
      displayName: this.form.displayName.trim() || null,
    };
    try {
      if (this.form.id == null) {
        await this.service?.create(body as never);
      } else {
        await this.service?.patch(this.form.id, body as never);
      }
      this.rootStore.notificationStore?.addSuccessNotification(
        this.form.id == null ? "Git identity linked" : "Git identity updated",
      );
      await this.loadIdentities();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error ? err.message : "Could not save the git identity";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  // ---- Remove dialog -----------------------------------------------------------

  requestRemove(row: GitIdentity) {
    this.removeTarget = row;
    this.removeError = null;
    this.rootStore.routerStore?.openDialog?.({
      dialogName: "RemoveGitIdentityDialog",
    });
  }

  cancelRemove() {
    if (this.removing) return;
    this.removeTarget = null;
  }

  async executeRemove(): Promise<boolean> {
    const target = this.removeTarget;
    if (!target || this.removing) return false;
    this.removing = true;
    this.removeError = null;
    try {
      await this.service?.remove(target.id);
      runInAction(() => {
        this.removeTarget = null;
      });
      await this.loadIdentities();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.removeError =
            err instanceof Error ? err.message : "Could not remove the git identity";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.removing = false;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.removeError = null;
    this.loading = false;
    this.error = null;
    this.searchQuery = "";
    this.form = { ...EMPTY_FORM };
    this.formError = null;
    this.saving = false;
    this.removeTarget = null;
    this.removing = false;
  }
}

export default GitIdentitiesStore;
